import {useEffect, useMemo, useRef, useState} from 'react';
import {StaticPageTitle} from '@common/seo/static-page-title';
import {SiteHeader} from '@common/ui/navigation/site-header';
import {Trans} from '@common/i18n/trans';
import {useNavigate} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import {useAuth} from '@common/auth/use-auth';
import {useValueLists} from '@common/http/value-lists';
import {apiClient, queryClient} from '@common/http/query-client';
import {useChangeLocale} from '@common/i18n/change-locale';
import {useSettings} from '@common/core/settings/use-settings';
import {useBootstrapData} from '@common/core/bootstrap-data/bootstrap-data-context';
import {getLocalTimeZone} from '@internationalized/date';
import {useUser} from '@common/auth/ui/use-user';
import {useStorageSummary} from '../drive/layout/sidebar/storage-summary/storage-summary';
import {DriveQueryKeys} from '../drive/drive-query-keys';
import {useInvoices} from '@common/billing/billing-page/requests/use-invoices';
import {prettyQuotaBytes} from '@common/uploads/utils/pretty-bytes';
import {
  deriveMasterKey,
  exportMasterJwk,
  exportRsaPrivateJwk,
  exportRsaPublicJwk,
  generateRecoveryKey,
  generateRsaKeyPair,
  sha256Hex,
} from '@common/e2ee/e2ee-crypto';
import {
  clearMasterKey,
  saveMasterKeyJwk,
  saveRsaPrivateJwk,
  saveTrustedMasterKeyJwk,
  saveTrustedRsaPrivateJwk,
} from '@common/e2ee/e2ee-session';
import {TransferAddonCheckoutDialog} from '../send/transfer-addon-checkout-dialog';
import type {TransferAddonSku} from '../send/transfer-addon-api';
import {StorageAddonCheckoutDialog} from './storage-addon-checkout-dialog';
import {
  STORAGE_ADDON_MAX_TOTAL_BYTES,
  STORAGE_ADDON_OPTIONS,
  type StorageAddonSku,
} from './storage-addon-api';
import {useDeleteEntries} from '../drive/files/queries/use-delete-entries';
import {formatTimezoneOptionLabel} from '@common/utils/format-timezone-utc-label';

function formatPrice(amount: number, currency: string) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(amount / 100);
}

function formatPlanPriceLine(
  amount?: number,
  currency?: string,
  interval?: string,
  intervalCount?: number,
) {
  if (
    amount === undefined ||
    !currency ||
    !interval ||
    intervalCount === undefined
  ) {
    return '';
  }

  const base = formatPrice(amount, currency);
  if (intervalCount === 1) {
    return `${base}/${interval}`;
  }

  return `${base} every ${intervalCount} ${interval}s`;
}

function formatDateLabel(date: string) {
  return new Date(date).toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTransferResetLabel(date?: string | null) {
  if (!date) return 'Reset date unavailable';

  const renewAt = new Date(date);
  const diffMs = renewAt.getTime() - Date.now();
  const days = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));

  return `Resets in ${days} day${days === 1 ? '' : 's'} - ${renewAt.toLocaleDateString(
    undefined,
    {month: 'long', day: 'numeric'},
  )}`;
}

function clampPercentage(value: number) {
  return Math.max(0, Math.min(100, value));
}

const STATIC_TEST_STORAGE = {
  available: 5 * 1024 * 1024 * 1024,
  used: 2 * 1024 * 1024 * 1024,
};

const STATIC_TEST_TRANSFER = {
  available: 10 * 1024 * 1024 * 1024,
  used: 3 * 1024 * 1024 * 1024,
};

export function SettingsPage() {
  const navigate = useNavigate();
  const {user} = useAuth();
  const settings = useSettings();
  const {mergeBootstrapData} = useBootstrapData();
  const userQuery = useUser('me', {
    with: ['subscriptions.product', 'subscriptions.price'],
  });
  const currentUser = userQuery.data?.user ?? user;
  const currentSubscription =
    currentUser?.subscriptions?.find(sub => sub.valid) ?? null;
  const storageSummaryQuery = useStorageSummary();
  const storageSummary = storageSummaryQuery.data;
  const invoicesQuery = useInvoices(currentUser?.id);
  const deleteEntries = useDeleteEntries();
  const changeLocale = useChangeLocale();
  const {data: valueLists} = useValueLists([
    'timezones',
    'countries',
    'localizations',
  ]);
  const [activeTab, setActiveTab] = useState<
    'advanced' | 'storage' | 'billing' | 'datetime'
  >('advanced');
  const [autoPurchaseEnabled, setAutoPurchaseEnabled] = useState(
    !!user?.transfer_addon_auto_purchase,
  );
  const [isCleanupModalOpen, setIsCleanupModalOpen] = useState(false);
  const [isAutoPurchasePopupOpen, setIsAutoPurchasePopupOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(
    currentUser?.language || '',
  );
  const [selectedCountry, setSelectedCountry] = useState(
    currentUser?.country || '',
  );
  const [selectedTimezone, setSelectedTimezone] = useState(
    currentUser?.timezone || getLocalTimeZone(),
  );
  const [isSavingLocalization, setIsSavingLocalization] = useState(false);
  const [isSavingAutoPurchase, setIsSavingAutoPurchase] = useState(false);
  const [autoPurchaseError, setAutoPurchaseError] = useState<string | null>(
    null,
  );
  const [isE2eeLoading, setIsE2eeLoading] = useState(false);
  const [e2eeError, setE2eeError] = useState<string | null>(null);
  const [e2eeRecoveryKey, setE2eeRecoveryKey] = useState<string | null>(null);
  const [e2eeConfirmedSaved, setE2eeConfirmedSaved] = useState(false);
  const [isE2eeKeyCopied, setIsE2eeKeyCopied] = useState(false);
  const [selectedTransferAddon, setSelectedTransferAddon] = useState<{
    sku: TransferAddonSku;
    price: string;
    limit: string;
  } | null>(null);
  const [isStorageUpgradeOpen, setIsStorageUpgradeOpen] = useState(false);
  const [selectedStorageAddon, setSelectedStorageAddon] = useState<{
    sku: StorageAddonSku;
    price: string;
    label: string;
  } | null>(null);
  const timezoneOptions = useMemo(
    () =>
      Object.values(valueLists?.timezones || {}).flatMap(sectionItems =>
        sectionItems.map(timezone => ({
          value: timezone.value,
          label: formatTimezoneOptionLabel(timezone.value, timezone.text),
        })),
      ),
    [valueLists?.timezones],
  );
  const cleanupModalRef = useRef<HTMLDivElement | null>(null);
  const autoPurchaseRef = useRef<HTMLDivElement | null>(null);
  const storageUpgradeRef = useRef<HTMLDivElement | null>(null);
  const regionSelectRef = useRef<HTMLSelectElement | null>(null);
  const timezoneSelectRef = useRef<HTMLSelectElement | null>(null);
  const languageSelectRef = useRef<HTMLSelectElement | null>(null);
  const trashQuery = useQuery({
    queryKey: ['settings-trash-summary'],
    queryFn: () =>
      apiClient
        .get('drive/file-entries', {
          params: {deletedOnly: true, perPage: 1, page: 1},
        })
        .then(response => response.data),
  });

  const storageAvailable =
    storageSummary?.available ??
    currentUser?.available_space ??
    STATIC_TEST_STORAGE.available;
  const storageUsed = storageSummary?.used ?? STATIC_TEST_STORAGE.used;
  const storageRemaining =
    storageAvailable === null || storageAvailable === undefined
      ? null
      : Math.max(storageAvailable - storageUsed, 0);
  const storagePercentage = clampPercentage(
    storageSummary?.percentage ??
      (storageAvailable ? (storageUsed * 100) / storageAvailable : 0),
  );

  const transferUsed = storageSummary?.transferUsed ?? STATIC_TEST_TRANSFER.used;
  const transferAvailable =
    storageSummary?.transferAvailable ??
    currentSubscription?.product?.monthly_transfer_bytes ??
    STATIC_TEST_TRANSFER.available;
  const transferRemaining =
    transferAvailable === null || transferAvailable === undefined
      ? null
      : Math.max(transferAvailable - transferUsed, 0);
  const transferPercentage = clampPercentage(
    storageSummary?.transferPercentage ??
      (transferAvailable ? (transferUsed * 100) / transferAvailable : 0),
  );

  const invoices = invoicesQuery.data?.invoices ?? [];
  const planFeatureList = currentSubscription?.product?.feature_list ?? [];
  const isLifetimePlan =
    currentSubscription?.price?.billing_type === 'one_time' ||
    currentSubscription?.product?.buy_once;
  const paymentMethodLabel =
    currentUser?.card_brand && currentUser?.card_last_four
      ? `${currentUser.card_brand} ending in ${currentUser.card_last_four}`
      : 'No payment method on file';
  const paymentMethodExpiry = currentUser?.card_expires || null;
  const canEditPaymentMethod = !!currentSubscription && !!currentUser?.card_brand;
  const trashPagination = trashQuery.data?.data
    ? trashQuery.data
    : trashQuery.data?.pagination;
  const trashItemCount = trashPagination?.total ?? 0;
  const trashSummaryLabel = trashQuery.isLoading
    ? 'Loading trash...'
    : trashItemCount === 1
      ? '1 item in trash'
      : `${trashItemCount} items in trash`;
  const canEmptyTrash = trashItemCount > 0 && !deleteEntries.isLoading;

  useEffect(() => {
    setSelectedLanguage(currentUser?.language || '');
    setSelectedCountry(currentUser?.country || '');
    setSelectedTimezone(currentUser?.timezone || getLocalTimeZone());
  }, [currentUser?.country, currentUser?.language, currentUser?.timezone]);

  useEffect(() => {
    setAutoPurchaseEnabled(!!currentUser?.transfer_addon_auto_purchase);
  }, [currentUser?.transfer_addon_auto_purchase]);

  useEffect(() => {
    if (!isCleanupModalOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (cleanupModalRef.current && !cleanupModalRef.current.contains(target)) {
        setIsCleanupModalOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isCleanupModalOpen]);

  useEffect(() => {
    if (!isAutoPurchasePopupOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (autoPurchaseRef.current && !autoPurchaseRef.current.contains(target)) {
        closeAutoPurchasePopup();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isAutoPurchasePopupOpen]);

  useEffect(() => {
    if (!isStorageUpgradeOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        storageUpgradeRef.current &&
        !storageUpgradeRef.current.contains(target)
      ) {
        setIsStorageUpgradeOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isStorageUpgradeOpen]);

  const handleCleanup = () => {
    if (!canEmptyTrash) return;
    deleteEntries.mutate(
      {entryIds: [], emptyTrash: true},
      {
        onSuccess: async () => {
          setIsCleanupModalOpen(false);
          await trashQuery.refetch();
        },
      },
    );
  };

  const persistAutoPurchaseSetting = async (
    enabled: boolean,
    sku?: TransferAddonSku,
  ) => {
    setAutoPurchaseError(null);
    setIsSavingAutoPurchase(true);

    try {
      await apiClient.put('billing/transfer-addon/auto-purchase', {
        enabled,
        sku: enabled ? sku : undefined,
      });

      setAutoPurchaseEnabled(enabled);
      if (user) {
        mergeBootstrapData({
          user: {
            ...user,
            transfer_addon_auto_purchase: enabled,
            transfer_addon_auto_purchase_sku: enabled ? (sku ?? null) : null,
          },
        });
      }
      await userQuery.refetch();
      return true;
    } catch (err: any) {
      setAutoPurchaseError(
        err?.response?.data?.message ||
          err?.message ||
          'Could not update auto-purchase setting.',
      );
      return false;
    } finally {
      setIsSavingAutoPurchase(false);
    }
  };

  const handleAutoPurchaseToggle = async () => {
    if (autoPurchaseEnabled) {
      await persistAutoPurchaseSetting(false);
      setIsAutoPurchasePopupOpen(false);
      return;
    }

    // enabling only persists once an amount is chosen and checkout succeeds
    setIsAutoPurchasePopupOpen(true);
  };

  const closeAutoPurchasePopup = () => {
    setIsAutoPurchasePopupOpen(false);
  };

  const openStripeModalForOption = (option: {
    label: '+20GB' | '+50GB';
    price: '$2.99' | '$4.99';
    transferLimit: '+20GB Transfer Limit' | '+50GB Transfer Limit';
  }) => {
    setSelectedTransferAddon({
      sku: option.label === '+50GB' ? '50gb' : '20gb',
      price: option.price,
      limit: option.transferLimit,
    });
  };

  const handleUpgradeClick = () => {
    if (isLifetimePlan) {
      setIsStorageUpgradeOpen(v => !v);
      return;
    }
    navigate('/pricing');
  };

  const lifetimeStorageOptions = useMemo(() => {
    // Prefer live space usage (includes storage_bonus_bytes) so options
    // update after purchase without relying on the user payload.
    const currentTotal = Number(
      storageSummary?.available ??
        currentSubscription?.product?.available_space ??
        0,
    );
    const remaining = Math.max(
      0,
      STORAGE_ADDON_MAX_TOTAL_BYTES - currentTotal,
    );
    return STORAGE_ADDON_OPTIONS.filter(
      option => option.bonusBytes <= remaining,
    );
  }, [
    storageSummary?.available,
    currentSubscription?.product?.available_space,
  ]);

  const handleSaveLocalization = async () => {
    setIsSavingLocalization(true);
    try {
      await apiClient.put('users/me', {
        language: selectedLanguage || undefined,
        country: selectedCountry || undefined,
        timezone: selectedTimezone || undefined,
      });
      if (selectedLanguage) {
        changeLocale.mutate({locale: selectedLanguage});
      }
      await queryClient.invalidateQueries({queryKey: ['users']});
      await userQuery.refetch();
    } finally {
      setIsSavingLocalization(false);
    }
  };

  const handleCopyE2eeKey = async () => {
    if (!e2eeRecoveryKey) return;

    try {
      await navigator.clipboard.writeText(e2eeRecoveryKey);
      setIsE2eeKeyCopied(true);
      window.setTimeout(() => setIsE2eeKeyCopied(false), 1200);
    } catch {
      setE2eeError('Could not copy recovery key.');
    }
  };

  const confirmEnableE2ee = async () => {
    if (!e2eeRecoveryKey || !e2eeConfirmedSaved) return;

    setE2eeError(null);
    setIsE2eeLoading(true);
    try {
      const hash = await sha256Hex(e2eeRecoveryKey);
      const master = await deriveMasterKey(e2eeRecoveryKey);
      const jwk = await exportMasterJwk(master);
      const rsaPair = await generateRsaKeyPair();
      const rsaPublicJwk = await exportRsaPublicJwk(rsaPair.publicKey);
      const rsaPrivateJwk = await exportRsaPrivateJwk(rsaPair.privateKey);

      saveMasterKeyJwk(jwk);
      saveTrustedMasterKeyJwk(jwk);
      saveRsaPrivateJwk(rsaPrivateJwk);
      saveTrustedRsaPrivateJwk(rsaPrivateJwk);

      await apiClient.post('user/e2ee/enable', {
        recovery_key_hash: hash,
        e2ee_public_key: rsaPublicJwk,
      });

      setE2eeRecoveryKey(null);
      setE2eeConfirmedSaved(false);
      setIsE2eeKeyCopied(false);
      await queryClient.invalidateQueries({queryKey: ['users']});
      await userQuery.refetch();
    } catch (err: any) {
      setE2eeError(
        err?.response?.data?.message || err?.message || 'Could not enable E2EE.',
      );
    } finally {
      setIsE2eeLoading(false);
    }
  };

  const handleToggleE2ee = async () => {
    if (!currentUser) return;

    setE2eeError(null);

    if (currentUser.e2ee_enabled_at) {
      const confirmed = window.confirm(
        'Disable E2EE on your account? New uploads will use standard encryption. Existing E2EE files stay encrypted.',
      );
      if (!confirmed) return;
    }

    setIsE2eeLoading(true);
    try {
      if (currentUser.e2ee_enabled_at) {
        await apiClient.post('user/e2ee/disable');
        clearMasterKey(false);
      } else {
        try {
          await apiClient.post('user/e2ee/enable');
        } catch (err: any) {
          const message = err?.response?.data?.message || err?.message;
          if (
            message !==
            'Recovery key is required the first time you enable E2EE.'
          ) {
            setE2eeError(message || 'Could not enable E2EE.');
            return;
          }

          setE2eeRecoveryKey(generateRecoveryKey());
          setE2eeConfirmedSaved(false);
          setIsE2eeKeyCopied(false);
          return;
        }
      }

      await queryClient.invalidateQueries({queryKey: ['users']});
      await userQuery.refetch();
    } catch (err: any) {
      setE2eeError(
        err?.response?.data?.message || err?.message || 'Could not update E2EE.',
      );
    } finally {
      setIsE2eeLoading(false);
    }
  };

  const openSelectFromIcon = (selectRef: {current: HTMLSelectElement | null}) => {
    const select = selectRef.current;
    if (!select || isSavingLocalization) return;
    select.focus();
    if (typeof select.showPicker === 'function') {
      select.showPicker();
      return;
    }
    select.click();
  };

  return (
    <div className="min-h-screen bg-[#f5f5f7] text-main dark:bg-[#14161a] dark:text-[#f3f4f6]">
      <StaticPageTitle>
        <Trans message="Settings" />
      </StaticPageTitle>

      <SiteHeader />

      <div className="relative mx-auto w-full max-w-[1895px] px-[6px] pb-[24px] lg:px-[12px] lg:pb-[25px]">
        <div className="mx-auto mt-24 max-w-[1207px] rounded-[60px] border-2 border-divider bg-paper p-20 md:mt-40 md:min-h-[709px] md:p-28">
          <div className="grid grid-cols-1 gap-20 lg:grid-cols-[210px_minmax(0,1fr)] lg:gap-28">
            <aside className="rounded-[24px] border-2 border-divider bg-paper py-10 md:h-[269px]">
              <button
                type="button"
                onClick={() => setActiveTab('advanced')}
                className={`mx-8 flex h-[65px] w-[calc(100%-16px)] items-center justify-center rounded-[22px] border-2 px-6 text-center text-[16px] leading-none ${
                  activeTab === 'advanced'
                    ? 'border-[#2B7E20] font-bold text-[#2B7E20]'
                    : 'border-transparent text-main'
                }`}
              >
                Advanced Protection
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('storage')}
                className={`mx-8 mt-8 flex h-[52px] w-[calc(100%-16px)] items-center justify-center rounded-[22px] border-2 px-6 text-center text-[16px] leading-none ${
                  activeTab === 'storage'
                    ? 'border-[#2B7E20] font-bold text-[#2B7E20]'
                    : 'border-transparent text-main'
                }`}
              >
                Storage
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('billing')}
                className={`mx-8 mt-8 flex h-[52px] w-[calc(100%-16px)] items-center justify-center whitespace-nowrap rounded-[22px] border-2 px-4 text-center text-[14px] leading-none ${
                  activeTab === 'billing'
                    ? 'border-[#2B7E20] font-bold text-[#2B7E20]'
                    : 'border-transparent text-main'
                }`}
              >
                Subscriptions &amp; Billing
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('datetime')}
                className={`mx-8 mt-8 flex h-[52px] w-[calc(100%-16px)] items-center justify-center whitespace-nowrap rounded-[22px] border-2 px-4 text-center text-[14px] leading-none ${
                  activeTab === 'datetime'
                    ? 'border-[#2B7E20] font-bold text-[#2B7E20]'
                    : 'border-transparent text-main'
                }`}
              >
                Area, time and language
              </button>
            </aside>

            <main>
              {activeTab === 'advanced' && (
                <div className="mx-auto max-w-[811px] rounded-[24px] border-2 border-divider bg-paper px-18 py-16 md:px-24 md:py-20">
                  <p className="text-[16px] font-bold leading-[1.35] text-main">
                    When enabled, new uploads from this browser session can be
                    encrypted before they leave your device. Save your recovery key
                    -- we cannot reset it
                  </p>
                  {e2eeError ? (
                    <p className="mt-16 text-center text-[14px] font-medium text-[#E34444]">
                      {e2eeError}
                    </p>
                  ) : null}
                  <div className="mt-28 flex justify-center">
                    <button
                      type="button"
                      onClick={() => void handleToggleE2ee()}
                      disabled={isE2eeLoading}
                      className="h-[52px] rounded-[20px] border border-[#2B7E20] bg-[#2B7E20] px-24 text-[14px] font-medium text-white disabled:opacity-70 dark:border-[#2B7E20] dark:bg-[#2B7E20]"
                    >
                      {currentUser?.e2ee_enabled_at
                        ? isE2eeLoading
                          ? 'Updating...'
                          : 'Disable E2EE'
                        : isE2eeLoading
                          ? 'Enabling...'
                          : 'Enable E2EE'}
                    </button>
                  </div>
                </div>
              )}
              {activeTab === 'storage' && (
                <div className="mx-auto max-w-[811px] space-y-28">
                  <section className="rounded-[24px] border-2 border-divider bg-paper px-18 py-18 md:px-22 md:py-18">
                    <div className="relative flex flex-wrap items-start justify-between gap-12">
                      <h2 className="text-[20px] font-bold leading-none text-[#d8562a]">
                        Your Storage
                      </h2>
                      <button
                        type="button"
                        onClick={() => setIsCleanupModalOpen(v => !v)}
                        aria-expanded={isCleanupModalOpen}
                        aria-controls="storage-cleanup-modal"
                        className={`h-[52px] rounded-[20px] border border-[#2B7E20] px-24 text-[14px] font-medium ${
                          isCleanupModalOpen
                            ? 'bg-paper text-main'
                            : 'bg-[#2B7E20] text-white'
                        }`}
                      >
                        Clean up
                      </button>
                      {isCleanupModalOpen ? (
                        <div
                          id="storage-cleanup-modal"
                          ref={cleanupModalRef}
                          role="dialog"
                          aria-modal="true"
                          aria-label="Cleanup storage"
                          className="absolute right-0 top-[66px] z-20 w-[350px] rounded-[30px] border-2 border-divider bg-paper p-20 shadow-[1px_1px_50px_1px_rgba(55,55,55,0.25)]"
                        >
                          <div className="flex items-center gap-10 rounded-[24px] border-2 border-divider bg-paper pl-14 py-9">
                            <span className="text-[16px] font-normal leading-none text-main">
                              {trashSummaryLabel}
                            </span>
                            <button
                              type="button"
                              onClick={handleCleanup}
                              disabled={!canEmptyTrash}
                              className="ml-auto h-[42px] rounded-[20px] border border-[#2B7E20] bg-[#2B7E20] px-18 text-[14px] font-medium text-white disabled:opacity-60"
                            >
                              {deleteEntries.isLoading ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>

                    <div className="mt-70">
                      <p className="text-center text-[20px] font-bold leading-none text-main">
                        {storageRemaining === null
                          ? 'Unlimited storage'
                          : `${prettyQuotaBytes(storageRemaining)} Remaining`}
                      </p>
                      <div className="mt-20 h-[25px] rounded-[33px] bg-[#e8e8e8] dark:bg-[#353535]">
                        <div
                          className="h-[25px] rounded-[100px] bg-[#93c5fd]"
                          style={{width: `${storagePercentage}%`}}
                        />
                      </div>
                      <p className="mt-[-22px] pr-14 text-right text-[16px] font-bold leading-none text-main">
                        {storageAvailable === null || storageAvailable === undefined
                          ? `${prettyQuotaBytes(storageUsed)} used`
                          : `${prettyQuotaBytes(storageUsed)}/${prettyQuotaBytes(
                              storageAvailable,
                            )}`}
                      </p>
                    </div>
                  </section>

                  <section className="rounded-[24px] border-2 border-divider bg-paper px-18 py-18 md:px-22 md:py-18">
                    <div className="flex flex-wrap items-start justify-between gap-12">
                      <div>
                        <h2 className="text-[20px] font-bold leading-none text-[#d8562a]">
                          Transfer Limit
                        </h2>
                        <p className="mt-10 text-[20px] font-bold leading-none text-main">
                          {formatTransferResetLabel(currentSubscription?.renews_at)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate('/drive', {state: {openShared: true}})}
                        className="h-[52px] rounded-[20px] border border-[#2B7E20] bg-[#2B7E20] px-18 text-[14px] font-medium text-white"
                      >
                        Check transfers
                      </button>
                    </div>

                    <div className="mt-18 flex justify-end">
                      <div className="relative" ref={autoPurchaseRef}>
                        {autoPurchaseError ? (
                          <p className="mb-10 max-w-[242px] text-right text-[13px] font-medium text-[#E34444]">
                            {autoPurchaseError}
                          </p>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => void handleAutoPurchaseToggle()}
                          disabled={isSavingAutoPurchase}
                          aria-pressed={autoPurchaseEnabled}
                          aria-expanded={isAutoPurchasePopupOpen}
                          aria-controls="auto-purchase-popup"
                          className={`flex h-[60px] w-[242px] items-center gap-10 rounded-[24px] border-2 bg-paper px-10 text-left disabled:opacity-70 ${
                            autoPurchaseEnabled
                              ? 'border-[#2B7E20]'
                              : 'border-[#E9ECF0]'
                          }`}
                        >
                          <span
                            aria-hidden="true"
                            className="pointer-events-none flex h-[25px] w-[25px] items-center justify-center rounded-[9px] bg-[#e9ecf0] dark:bg-[#2a2d33]"
                          >
                            {autoPurchaseEnabled ? (
                              <svg
                                viewBox="0 0 24 24"
                                className="h-[16px] w-[16px] text-[#2B7E20]"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            ) : null}
                          </span>
                          <span className="min-w-0 flex-1 text-[16px] font-bold leading-none text-main">
                            {autoPurchaseEnabled
                              ? 'Auto-purchase Enabled'
                              : 'Auto-purchase Disabled'}
                          </span>
                        </button>

                        {isAutoPurchasePopupOpen ? (
                          <div
                            id="auto-purchase-popup"
                            role="dialog"
                            aria-modal="false"
                            aria-label="Choose auto-purchase amount"
                            className="absolute right-0 top-[76px] z-20 w-[350px] rounded-[30px] border-2 border-divider bg-paper px-12 pb-12 pt-14 shadow-[1px_1px_50px_1px_rgba(55,55,55,0.25)]"
                          >
                            <p className="px-8 text-[16px] font-bold leading-[1.35] text-main">
                              Automatically increase transfer limit
                              <br />
                              (added transfers reset every billing cycle)
                            </p>
                            <div className="mt-10 space-y-8">
                              <button
                                type="button"
                                onClick={() =>
                                  openStripeModalForOption({
                                    label: '+20GB',
                                    price: '$2.99',
                                    transferLimit: '+20GB Transfer Limit',
                                  })
                                }
                                className="flex h-[60px] w-full items-center rounded-[24px] border-[2px] border-solid border-[#E9ECF0] bg-paper pl-16 pr-0 transition-colors hover:border-[#2B7E20]"
                              >
                                <span className="text-[17px] font-normal text-[#535353]">
                                  +20GB Transfer Limit
                                </span>
                                <span className="ml-auto flex h-[60px] w-[83px] items-center justify-center rounded-[24px] bg-[#2B7E20] text-[17px] font-normal text-white">
                                  $2.99
                                </span>
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  openStripeModalForOption({
                                    label: '+50GB',
                                    price: '$4.99',
                                    transferLimit: '+50GB Transfer Limit',
                                  })
                                }
                                className="flex h-[60px] w-full items-center rounded-[24px] border-[2px] border-solid border-[#E9ECF0] bg-paper pl-16 pr-0 transition-colors hover:border-[#2B7E20]"
                              >
                                <span className="text-[17px] font-normal text-[#535353]">
                                  +50GB Transfer Limit
                                </span>
                                <span className="ml-auto flex h-[60px] w-[83px] items-center justify-center rounded-[24px] bg-[#2B7E20] text-[17px] font-normal text-white">
                                  $4.99
                                </span>
                              </button>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="mt-22">
                      <p className="text-center text-[20px] font-bold leading-none text-main">
                        {transferRemaining === null
                          ? 'Unlimited transfer'
                          : `${prettyQuotaBytes(transferRemaining)} Remaining`}
                      </p>
                      <div className="mt-20 h-[25px] rounded-[33px] bg-[#e8e8e8] dark:bg-[#353535]">
                        <div
                          className="h-[25px] rounded-[100px] bg-[#93a5fd]"
                          style={{width: `${transferPercentage}%`}}
                        />
                      </div>
                      <p className="mt-[-22px] pr-14 text-right text-[16px] font-bold leading-none text-main">
                        {transferAvailable === null ||
                        transferAvailable === undefined
                          ? `${prettyQuotaBytes(transferUsed)} used`
                          : `${prettyQuotaBytes(transferUsed)}/${prettyQuotaBytes(
                              transferAvailable,
                            )}`}
                      </p>
                    </div>
                  </section>
                </div>
              )}
              {activeTab === 'billing' && (
                <div className="relative mx-auto max-w-[811px] space-y-18">
                  <section className="rounded-[24px] border-2 border-divider bg-paper px-14 py-14 md:px-20 md:py-18">
                    <div className="grid gap-16 md:grid-cols-[minmax(210px,1fr)_minmax(300px,1.5fr)] md:items-start">
                      <div>
                        <h2 className="text-[20px] font-bold leading-none text-[#d8562a]">
                          Current plan
                        </h2>
                        {!isLifetimePlan ? (
                          <p className="mt-8 text-[20px] font-bold leading-none text-main">
                            {formatPlanPriceLine(
                              currentSubscription?.price?.amount,
                              currentSubscription?.price?.currency,
                              currentSubscription?.price?.interval,
                              currentSubscription?.price?.interval_count,
                            ) || 'No active subscription'}
                          </p>
                        ) : (
                          <p className="mt-8 text-[20px] font-bold leading-[1.2] text-main">
                            Buy Once.
                            <br />
                            Keep forever.
                          </p>
                        )}
                      </div>
                      <ul className="space-y-8 text-[17px] leading-none text-main">
                        {planFeatureList.map(feature => (
                          <li key={feature}>{feature}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="mt-20 flex flex-wrap items-center gap-10 md:justify-end">
                      {currentSubscription ? (
                        <button
                          type="button"
                          onClick={() => navigate('/billing/cancel')}
                          className="h-[65px] w-[141px] rounded-[24px] border-2 border-[#2b7e20] px-18 text-[16px] font-normal text-main dark:text-[#f1f1f1]"
                        >
                          Cancel
                        </button>
                      ) : null}
                      <div className="relative" ref={storageUpgradeRef}>
                        <button
                          type="button"
                          onClick={handleUpgradeClick}
                          className="h-[65px] w-[141px] rounded-[24px] border border-[#2b7e20] bg-[#2b7e20] px-18 text-[16px] font-normal text-white"
                        >
                          Upgrade
                        </button>
                        {isLifetimePlan && isStorageUpgradeOpen ? (
                          <div
                            role="dialog"
                            aria-label="Upgrade storage"
                            className="absolute right-0 top-[calc(100%+8px)] z-20 w-[350px] rounded-[30px] border-2 border-divider bg-[#f8f9ff] px-12 pb-12 pt-14 shadow-[1px_1px_50px_1px_rgba(55,55,55,0.25)]"
                          >
                            <p className="px-8 text-[16px] font-bold leading-[1.35] text-main">
                              Add storage
                            </p>
                            <div className="mt-10 space-y-8">
                              {lifetimeStorageOptions.length === 0 ? (
                                <p className="px-8 text-[15px] text-[#535353]">
                                  You already have the maximum storage for this
                                  plan.
                                </p>
                              ) : (
                                lifetimeStorageOptions.map(option => (
                                  <button
                                    key={option.sku}
                                    type="button"
                                    onClick={() => {
                                      setIsStorageUpgradeOpen(false);
                                      setSelectedStorageAddon({
                                        sku: option.sku,
                                        price: option.price,
                                        label: option.label,
                                      });
                                    }}
                                    className="flex h-[60px] w-full items-center rounded-[24px] border-[2px] border-solid border-[#E9ECF0] bg-white pl-16 pr-0 transition-colors hover:border-[#2B7E20]"
                                  >
                                    <span className="text-[17px] font-normal text-[#535353]">
                                      {option.label}
                                    </span>
                                    <span className="ml-auto flex h-[60px] w-[83px] items-center justify-center rounded-[24px] bg-[#2B7E20] text-[17px] font-normal text-white">
                                      {option.price}
                                    </span>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        ) : null}
                      </div>
                      <p className="max-w-[192px] text-[16px] font-bold leading-[1.15] text-[#2b7e20]">
                        {isLifetimePlan
                          ? 'Up to 4TB storage for $90/TB!'
                          : currentSubscription?.renews_at
                            ? `Renews on ${formatDateLabel(
                                currentSubscription.renews_at,
                              )}`
                            : 'Choose a plan that fits your storage needs.'}
                      </p>
                    </div>
                  </section>

                  <div className="grid gap-18 md:grid-cols-2">
                    <section className="flex min-h-[254px] flex-col rounded-[24px] border-2 border-divider bg-paper px-16 py-16">
                      <h3 className="text-[20px] font-bold leading-none text-[#d8562a]">
                        Payment method
                      </h3>
                      <p className="mt-10 text-[20px] font-bold leading-none text-main">
                        {paymentMethodLabel}
                      </p>
                      {paymentMethodExpiry ? (
                        <p className="mt-12 text-[16px] font-medium leading-none text-main">
                          Expires {paymentMethodExpiry}
                        </p>
                      ) : null}
                      <div className="mt-auto flex justify-center pt-16">
                        <button
                          type="button"
                          onClick={() => navigate('/billing/change-payment-method')}
                          disabled={!canEditPaymentMethod}
                          className="h-[65px] w-[141px] rounded-[24px] border border-[#2b7e20] bg-[#2b7e20] px-18 text-[16px] font-normal text-white"
                        >
                          {canEditPaymentMethod ? 'Edit' : 'Unavailable'}
                        </button>
                      </div>
                    </section>

                    <section className="rounded-[24px] border-2 border-divider bg-paper px-16 py-16 dark:border-[#353535] dark:bg-[#1f1f1f]">
                      <h3 className="text-[20px] font-bold leading-none text-[#d8562a]">
                        Payment history
                      </h3>
                      <div className="drive-table-scroll mt-12 max-h-[174px] space-y-8 overflow-y-auto pr-1">
                        {(invoices.length
                          ? invoices.map(invoice => {
                              const amount = invoice.subscription.price?.amount;
                              const currency =
                                invoice.subscription.price?.currency;
                              const amountLabel =
                                amount !== undefined && currency
                                  ? formatPrice(amount, currency)
                                  : 'N/A';
                              return `${formatDateLabel(
                                invoice.created_at,
                              )} - ${amountLabel}`;
                            })
                          : ['No payment history found']).map(entry => (
                          <div
                            key={entry}
                            className="rounded-[20px] border-2 border-divider bg-alt px-14 py-8 text-center text-[17px] font-bold leading-none text-main"
                          >
                            {entry}
                          </div>
                        ))}
                      </div>
                    </section>
                  </div>
                </div>
              )}
              {activeTab === 'datetime' && (
                <div className="flex min-h-[580px] w-full items-center justify-center lg:min-h-[620px]">
                <div className="flex h-[434px] w-full max-w-[811px] items-center rounded-[24px] border-[2px] border-solid border-[#E9ECF0] bg-white px-24 dark:border-divider dark:bg-paper">
                    <div className="w-full space-y-16">
                      <div className="grid items-center gap-12 sm:grid-cols-[minmax(120px,175px)_minmax(0,1fr)]">
                        <label
                          htmlFor="settings-region"
                          className="text-[20px] font-bold leading-none text-[#d8562a]"
                        >
                          Region
                        </label>
                        <div className="relative h-[65px] w-full max-w-[411px]">
                          <select
                            id="settings-region"
                            ref={regionSelectRef}
                            value={selectedCountry}
                            disabled
                            aria-readonly="true"
                            className="h-full w-full cursor-not-allowed appearance-none rounded-[24px] bg-[#f4f4f4] px-16 pr-[72px] text-center text-[16px] font-bold text-[#989898] outline-none dark:bg-[#2a2d33] dark:text-[#c9ced6]"
                          >
                            <option value="">Select country</option>
                            {(valueLists?.countries || []).map(country => (
                              <option key={country.code} value={country.code}>
                                {country.name}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            disabled
                            aria-label="Region is set from account setup"
                            className="absolute right-0 top-0 flex h-[65px] w-[65px] cursor-not-allowed items-center justify-center rounded-[24px] border-2 border-[#E9ECF0] bg-white opacity-60 dark:bg-[#1f1f1f]"
                          >
                            <svg
                              aria-hidden="true"
                              viewBox="0 0 24 24"
                              className="h-[25px] w-[25px] text-[#535353] dark:text-[#c9ced6]"
                              fill="currentColor"
                            >
                              <path d="m12 15.5-5.5-8h11z" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      <div className="grid items-center gap-12 sm:grid-cols-[minmax(120px,175px)_minmax(0,1fr)]">
                        <label
                          htmlFor="settings-timezone"
                          className="text-[20px] font-bold leading-none text-[#d8562a]"
                        >
                          Timezone
                        </label>
                        <div className="relative h-[65px] w-full max-w-[411px]">
                          <select
                            id="settings-timezone"
                            ref={timezoneSelectRef}
                            value={selectedTimezone}
                            onChange={e => setSelectedTimezone(e.target.value)}
                            onBlur={handleSaveLocalization}
                            disabled={isSavingLocalization}
                            className="h-full w-full appearance-none rounded-[24px] bg-[#f4f4f4] px-16 pr-[72px] text-center text-[16px] font-bold text-[#989898] outline-none dark:bg-[#2a2d33] dark:text-[#c9ced6]"
                          >
                            <option value="">Select timezone</option>
                            {timezoneOptions.map(timezone => (
                              <option key={timezone.value} value={timezone.value}>
                                {timezone.label}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => openSelectFromIcon(timezoneSelectRef)}
                            disabled={isSavingLocalization}
                            aria-label="Open timezone list"
                            className="absolute right-0 top-0 flex h-[65px] w-[65px] items-center justify-center rounded-[24px] border-2 border-[#2B7E20] bg-white dark:bg-[#1f1f1f]"
                          >
                            <svg
                              aria-hidden="true"
                              viewBox="0 0 24 24"
                              className="h-[25px] w-[25px] text-[#535353] dark:text-[#c9ced6]"
                              fill="currentColor"
                            >
                              <path d="m12 15.5-5.5-8h11z" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      <div className="grid items-center gap-12 sm:grid-cols-[minmax(120px,175px)_minmax(0,1fr)]">
                        <label
                          htmlFor="settings-language"
                          className="text-[20px] font-bold leading-none text-[#d8562a]"
                        >
                          Language
                        </label>
                        <div className="relative h-[65px] w-full max-w-[411px]">
                          <select
                            id="settings-language"
                            ref={languageSelectRef}
                            value={selectedLanguage}
                            onChange={e => setSelectedLanguage(e.target.value)}
                            onBlur={handleSaveLocalization}
                            disabled={isSavingLocalization}
                            className="h-full w-full appearance-none rounded-[24px] bg-[#f4f4f4] px-16 pr-[72px] text-center text-[16px] font-bold text-[#989898] outline-none dark:bg-[#2a2d33] dark:text-[#c9ced6]"
                          >
                            <option value="">Select language</option>
                            {(valueLists?.localizations || []).map(localization => (
                              <option
                                key={localization.language}
                                value={localization.language}
                              >
                                {localization.name}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => openSelectFromIcon(languageSelectRef)}
                            disabled={isSavingLocalization}
                            aria-label="Open language list"
                            className="absolute right-0 top-0 flex h-[65px] w-[65px] items-center justify-center rounded-[24px] border-2 border-[#2B7E20] bg-white dark:bg-[#1f1f1f]"
                          >
                            <svg
                              aria-hidden="true"
                              viewBox="0 0 24 24"
                              className="h-[25px] w-[25px] text-[#535353] dark:text-[#c9ced6]"
                              fill="currentColor"
                            >
                              <path d="m12 15.5-5.5-8h11z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                </div>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
      <TransferAddonCheckoutDialog
        open={!!selectedTransferAddon}
        sku={selectedTransferAddon?.sku ?? '20gb'}
        price={selectedTransferAddon?.price ?? '$2.99'}
        limit={selectedTransferAddon?.limit ?? '+20GB Transfer Limit'}
        onClose={() => setSelectedTransferAddon(null)}
        onSuccess={() => {
          const purchasedSku = selectedTransferAddon?.sku ?? '20gb';
          setSelectedTransferAddon(null);
          setIsAutoPurchasePopupOpen(false);
          void persistAutoPurchaseSetting(true, purchasedSku);
        }}
      />
      <StorageAddonCheckoutDialog
        open={!!selectedStorageAddon}
        sku={selectedStorageAddon?.sku ?? '1tb'}
        price={selectedStorageAddon?.price ?? '$90.00'}
        label={selectedStorageAddon?.label ?? '+1TB storage'}
        onClose={() => setSelectedStorageAddon(null)}
        onSuccess={() => {
          setSelectedStorageAddon(null);
          setIsStorageUpgradeOpen(false);
          void userQuery.refetch();
          void queryClient.invalidateQueries(DriveQueryKeys.fetchStorageSummary);
        }}
      />
      {e2eeRecoveryKey ? (
        <div
          className="fixed inset-0 z-[190] flex items-center justify-center bg-black/30 px-16"
          aria-hidden={false}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Save recovery key"
            className="w-full max-w-[520px] rounded-[28px] border-2 border-divider bg-paper p-24 shadow-[1px_1px_50px_1px_rgba(55,55,55,0.25)]"
          >
            <p className="text-center text-[22px] font-bold leading-none text-main">
              Save your recovery key
            </p>
            <p className="mt-16 text-center text-[15px] font-medium leading-[1.4] text-main">
              Store this key somewhere safe. It will not be shown again and you
              will need it to recover encrypted files.
            </p>
            <div className="mt-20 flex items-center gap-10">
              <div
                className="min-w-0 flex-1 rounded-[20px] border-2 border-divider bg-alt px-14 py-12 font-mono text-[13px] font-medium text-main"
                title={e2eeRecoveryKey}
              >
                <span className="block overflow-hidden text-ellipsis whitespace-nowrap">
                  {e2eeRecoveryKey}
                </span>
              </div>
              <button
                type="button"
                onClick={() => void handleCopyE2eeKey()}
                className="h-[52px] rounded-[20px] border border-[#2B7E20] bg-[#2B7E20] px-18 text-[14px] font-medium text-white"
              >
                {isE2eeKeyCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <label className="mt-18 flex items-start gap-10 rounded-[20px] border-2 border-divider bg-alt px-14 py-12">
              <input
                type="checkbox"
                checked={e2eeConfirmedSaved}
                onChange={e => setE2eeConfirmedSaved(e.target.checked)}
                className="mt-1"
                aria-label="I have saved my recovery key"
              />
              <span className="text-[14px] font-medium leading-[1.4] text-main">
                I have copied this recovery key and saved it in a safe place.
              </span>
            </label>
            <button
              type="button"
              onClick={() => void confirmEnableE2ee()}
              disabled={!e2eeConfirmedSaved || isE2eeLoading}
              className="mt-20 h-[52px] w-full rounded-[20px] border border-[#2B7E20] bg-[#2B7E20] px-24 text-[14px] font-medium text-white disabled:opacity-60"
            >
              {isE2eeLoading ? 'Enabling...' : 'Confirm & enable E2EE'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
